import React, { useState } from 'react';
import type { ReactElement } from 'react';
import {
  H2,
  Card,
  FigCaption,
  spacingHelper,
  Button,
  CrossMd,
  Link,
  paletteHelper,
  Text,
  Stack,
  Carousel,
  Inline,
} from '@construct-kit/core';
import styled from 'styled-components';
import type { RecentSearchType } from '../../../shared/model/tsTypes';
import { deleteRecentSearch, getRecentSearches } from '../../../client/service/preferenceService';
import SetAlertContainer from './SetAlertContainer';
import { getCookie } from '../../../client/cookie/cookieService';
import AccountOverlay from '../../../shared/components/AccountOverlay';

export type HomepageRecentSearchesProps = {
  recentSearches: RecentSearchType[];
  trackRecentSearchClick: () => void;
  setRecentSearchesState: (recentSearches: RecentSearchType[]) => void;
};

const StyledTitle = styled(H2).attrs({ variant: 'title02' })`
  margin: ${spacingHelper('large 0 extraSmall medium')};
  ${({ theme }) => theme.layoutBreakpoints.large} {
    line-height: 2.25rem;
    font-size: 1.5rem;
    margin: ${spacingHelper('extraLarge 0 medium medium')};
  }
`;

const RecentSearchCarousel = styled(Carousel)`
  width: 100%;
  height: 100%;
`;

const RecentSearchCardContainer = styled.div`
  position: relative;
  margin-top: ${spacingHelper('twoExtraSmall')};
`;

const StyledLink = styled(Link)`
  display: inline-block;
  text-decoration: none;
  color: ${paletteHelper('textPrimary')};

  :hover {
    text-decoration: none;
    color: ${paletteHelper('textPrimary')};
  }
`;

const RecentSearchCard = styled(Card)`
  height: 7rem;
  width: 15rem;
`;

const StyledLocationsText = styled(Text)`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledDetailsText = styled(FigCaption)`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const DeleteButton = styled(Button)`
  position: absolute;
  right: ${spacingHelper('twoExtraSmall')};
  top: ${spacingHelper('extraSmall')};
  :hover {
    background-color: transparent;
  }
`;

const StyledLocationTextWrapper = styled(Inline)`
  width: 12rem;
`;

const splitLocationText = (
  locationText: string,
): { firstLocation: string; numberOfLocation?: string } => {
  if (locationText.endsWith(' more')) {
    return {
      firstLocation: locationText.split('and')[0],
      numberOfLocation: locationText.split('and')[1].split(' ')[1],
    };
  }
  return { firstLocation: locationText };
};

export const HomepageRecentSearches = ({
  recentSearches,
  trackRecentSearchClick,
  setRecentSearchesState,
}: HomepageRecentSearchesProps): ReactElement | null => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);

  const myRcaToken = getCookie('lmdstok');
  return recentSearches.length ? (
    <>
      <StyledTitle>Recent searches</StyledTitle>
      <RecentSearchCarousel insideGap="medium" outsideGap="medium">
        {recentSearches.map(({ name, detailText, url }, i) => (
          <RecentSearchCardContainer key={`${name}-${i}`}>
            <StyledLink href={url} onClick={trackRecentSearchClick}>
              <RecentSearchCard>
                <Stack gap="0" alignItems="flex-start" inset="small">
                  <StyledLocationTextWrapper gap="0" grow={false}>
                    <StyledLocationsText variant="subtitle01">
                      {splitLocationText(name).firstLocation}
                    </StyledLocationsText>
                    {splitLocationText(name).numberOfLocation && (
                      <Text variant="subtitle01">
                        {`+${splitLocationText(name).numberOfLocation}`}
                      </Text>
                    )}
                  </StyledLocationTextWrapper>
                  <StyledDetailsText variant="body02">{detailText}</StyledDetailsText>
                </Stack>
              </RecentSearchCard>
            </StyledLink>
            <DeleteButton
              variant="naked"
              hideLabel
              sizeVariant="small"
              compact
              icon={<CrossMd />}
              onClick={() => {
                deleteRecentSearch(url);
                const recentSearchesState = getRecentSearches() as RecentSearchType[];
                setRecentSearchesState(recentSearchesState);
              }}
            >
              Delete this saved search
            </DeleteButton>
            <SetAlertContainer
              openOverlay={() => setShowOverlay(true)}
              myRcaToken={myRcaToken}
              url={url}
            />
          </RecentSearchCardContainer>
        ))}
      </RecentSearchCarousel>
      <AccountOverlay
        isDisplayed={showOverlay}
        heading="Create an account to receive property alerts"
        content="Receive email alerts for new properties with a free account."
        image={
          <img
            alt=""
            src="/listing-ui-assets/img/property-alerts-background.svg"
            width="229"
            height="148"
          />
        }
        closeFn={() => {
          setShowOverlay(false);
        }}
      />
    </>
  ) : null;
};
